/* @flow */

import * as React from 'react';
import glamorous from 'glamorous';
import { css } from 'glamor';
import { delay, isEqual, filter, lte, omit, placeholder, where } from 'lodash/fp';
import { shallowEqual, withProps } from 'recompose';
import vipIcon from 'app/images/globalLeaderboard/blue-tick.svg';
import Avatar from 'app/components/commons/Avatar';
import CurrencyIcon from 'app/components/commons/CurrencyIcon';
import AnimateNumber from '../commons/AnimateNumber';
import LeaderRowPrefix from './LeaderRowPrefix';
import Responsive from '../responsive/Responsive';
import Row from '../commons/Row';
import PlayersCountDisplay from './PlayersCountDisplay';
import { PRIZE, STATUS } from '../../constants/leaderBoardColumnTypes';
import { NOT_FRIEND } from '../../constants/friendStatusTypes';
import { IN_PROGRESS } from '../../constants/challengeStatusTypes';

type Props = {
  avatar: Object,
  className?: string,
  countryImage: string,
  countryName?: string,
  displayCountry?: boolean,
  displayPlayersCount?: boolean,
  dynamicColumn?: PRIZE | STATUS,
  friendStatus?: number,
  id: string,
  isHighlighted?: boolean,
  isMarked?: boolean,
  isUltimateChampion?: boolean,
  isVIP?: boolean,
  name: string,
  onClick?: string => void,
  playersCount?: Array<Object>,
  prize?: string,
  rank: number,
  score: number,
  showCurrency?: boolean
};

type State = {
  diff: number,
  movingUp: boolean,
  movingDown: boolean
};

const RowWrapper = glamorous(Row)(
  ({ movingUp, theme }) =>
    movingUp
      ? {
          animationDuration: '3s',
          animationName: css.keyframes({
            '0%, 25%': {
              backgroundColor: theme.color.green
            },
            '100%': {
              backgroundColor: theme.color.white
            }
          }),
          animationTimingFunction: 'ease-in',
          zIndex: theme.zIndex.slideUp
        }
      : undefined,
  ({ movingDown, theme }) =>
    movingDown
      ? {
          zIndex: theme.zIndex.slideDown
        }
      : undefined
);

const RowInner = glamorous.div(
  {
    alignItems: 'center',
    display: 'flex',
    paddingTop: 4,
    paddingBottom: 4
  },
  ({ marked, theme }) =>
    marked
      ? {
          backgroundColor: theme.color.basis6
        }
      : undefined
);

const Main = glamorous.div({
  alignItems: 'center',
  display: 'flex',
  height: '100%',
  flexBasis: 0,
  flexGrow: 1,
  flexShrink: 1
});

const AvatarWrapper = glamorous.div(({ theme }) => ({
  marginRight: theme.spacing.small
}));

const Entrant = glamorous.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: 1,
  justifyContent: 'center',
  overflow: 'hidden',
  paddingRight: theme.spacing.default,
  wordBreak: 'break-all'
}));

const EntrantCountry = glamorous.div(({ theme }) => ({
  color: theme.color.basis2,
  fontSize: theme.fontSize.tiny,
  fontStyle: 'italic',
  lineHeight: 1,
  textTransform: 'uppercase'
}));

const EntrantName = glamorous.div({
  fontSize: '0.925rem'
});

const Cell = glamorous.div(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  height: '100%',
  justifyContent: 'center',
  flexBasis: 70,
  flexGrow: 0,
  flexShrink: 0,
  [theme.mediaQuery.desktopAndUp]: {
    flexBasis: 100
  }
}));

class LeaderRow extends React.Component<Props, State> {
  props: Props;

  state: State = {
    diff: 0,
    movingUp: false,
    movingDown: false
  };

  static defaultProps = {
    className: undefined,
    countryName: undefined,
    displayCountry: false,
    displayPlayersCount: false,
    dynamicColumn: undefined,
    friendStatus: NOT_FRIEND,
    isHighlighted: false,
    isMarked: false,
    isUltimateChampion: false,
    isVIP: false,
    onClick: undefined,
    prize: undefined,
    playersCount: undefined,
    showCurrency: true
  };

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { rank } = this.props;
    const { rank: nextRank } = nextProps;
    const delayedFunc = delay(4000);

    if (rank !== undefined && nextRank !== undefined) {
      const diff = rank - nextRank;

      if (diff > 0) {
        // The user is rising up the board! Let them know it's happening
        // so the animation can play
        this.setState({
          movingUp: true,
          diff
        });
        // reset it after the animation would be done
        delayedFunc(() => {
          this.setState({
            movingUp: false
          });
        });
      } else if (diff < 0) {
        this.setState({
          movingDown: true,
          diff
        });
        // reset it after the animation would be done
        delayedFunc(() => {
          this.setState({
            movingDown: false
          });
        });
      }
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    const omitFields = omit(['onClick', 'playersCount']);
    const filteredProps = omitFields(this.props);
    const filteredNextProps = omitFields(nextProps);
    const { playersCount } = this.props;

    return (
      !shallowEqual(filteredProps, filteredNextProps) ||
      !isEqual(playersCount, nextProps.playersCount) || // deep equal
      !shallowEqual(this.state, nextState)
    );
  }

  handleClick = () => {
    const { id, onClick } = this.props;

    if (onClick) {
      onClick(id);
    }
  };

  render() {
    const {
      avatar,
      className,
      countryImage,
      countryName,
      displayCountry,
      displayPlayersCount,
      dynamicColumn,
      friendStatus,
      isHighlighted,
      isMarked,
      isUltimateChampion,
      isVIP,
      name,
      playersCount,
      prize,
      rank,
      score,
      showCurrency
    } = this.props;
    const { diff, movingUp, movingDown } = this.state;

    return (
      <RowWrapper
        active={isHighlighted}
        className={className}
        movingDown={movingDown}
        movingUp={movingUp}
        onTap={this.handleClick}
      >
        <RowInner marked={isMarked}>
          <LeaderRowPrefix
            active={isHighlighted}
            change={diff}
            isMarked={isMarked}
            movingUp={movingUp}
            rank={rank}
          />
          <Main>
            <AvatarWrapper>
              <Avatar
                avatar={avatar}
                countryFlag={displayCountry ? countryImage : undefined}
                hasCrown={isUltimateChampion}
                hasWhiteBackground
                size="xsmall"
                status={friendStatus}
              />
            </AvatarWrapper>
            <Entrant>
              <EntrantName>
                {name}
                {isVIP && (
                  <span>
                    {' '}
                    <img src={vipIcon} alt="" />
                  </span>
                )}
              </EntrantName>
              {displayCountry && <EntrantCountry>{countryName}</EntrantCountry>}
            </Entrant>
          </Main>
          <Responsive
            mobile={!displayPlayersCount || dynamicColumn === PRIZE}
            tabletLandscape
            desktop
          >
            <Cell>
              {prize && showCurrency && <CurrencyIcon />}
              {prize}
            </Cell>
          </Responsive>
          {displayPlayersCount && (
            <Responsive mobile={dynamicColumn === STATUS} tabletLandscape desktop>
              <Cell>
                {playersCount && <PlayersCountDisplay data={playersCount} scheme="dark" />}
              </Cell>
            </Responsive>
          )}
          <Cell>
            <AnimateNumber number={score} format="0.00" />
          </Cell>
        </RowInner>
      </RowWrapper>
    );
  }
}

const filterActive = filter(
  where({
    id: lte(placeholder, IN_PROGRESS)
  })
);

const enhancer = withProps(({ playersCount }) => ({
  playersCount: filterActive(playersCount)
}));

export default enhancer(LeaderRow);
